import { PDFDocument, PDFFont, PDFImage, PDFPage, StandardFonts, rgb } from 'pdf-lib';

import affidavitSidebarUrl from './assets/affidavit-sidebar.png';
import torriMontgomerySignatureUrl from './assets/torri-montgomery-signature.png';

export type CandidateAffidavitType = 'odrh' | 'mhd';

type AffidavitSnapshot = {
  affidavitType: CandidateAffidavitType;
  name: string;
  bookingNumber: string;
  caseNumber: string;
  diagnosis: string;
  nextCourtDate: string;
  priorProgram: string;
  priorCaseNumbers: string;
  generatedDate: string;
};

type TextRun = {
  content: string;
  bold?: boolean;
  italic?: boolean;
};

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const CONTENT_LEFT = 168;
const CONTENT_RIGHT = 24;
const CONTENT_BOTTOM = 24;
const CONTENT_WIDTH = PAGE_WIDTH - CONTENT_LEFT - CONTENT_RIGHT;
const BODY_FONT_SIZE = 9.3;
const BODY_LINE_HEIGHT = 11.5;

const text = (value: unknown): string => String(value ?? '').trim();

const pacificDate = (value = new Date()): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(value);

const affidavitDisplayDate = (value: unknown): string => {
  if (!value) return '';
  const raw = text(value);
  const parsed = value instanceof Date
    ? value
    : new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00Z` : raw);
  return Number.isNaN(parsed.getTime()) ? raw : pacificDate(parsed);
};

export const buildAffidavitSnapshot = (
  candidate: Record<string, unknown>,
  affidavitType: CandidateAffidavitType,
): AffidavitSnapshot => {
  const snapshot = {
    affidavitType,
    name: text(candidate.name) || `${text(candidate.first_name)} ${text(candidate.last_name)}`.trim(),
    bookingNumber: text(candidate.n_bk),
    caseNumber: text(candidate.n_case),
    diagnosis: text(candidate.dx),
    nextCourtDate: affidavitDisplayDate(candidate.next_court_date),
    priorProgram: text(candidate.prior_program),
    priorCaseNumbers: text(candidate.prior_case_numbers),
    generatedDate: pacificDate(),
  };
  const missing = [
    ['name', snapshot.name],
    ['booking number', snapshot.bookingNumber],
    ['case number', snapshot.caseNumber],
    ['diagnosis', snapshot.diagnosis],
    ['next court date', snapshot.nextCourtDate],
  ].filter(([, value]) => !value).map(([label]) => label);
  if (missing.length > 0) {
    throw new Error(`Complete these affidavit fields first: ${missing.join(', ')}`);
  }
  return snapshot;
};

const fetchAsset = async (url: string): Promise<ArrayBuffer> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load affidavit asset (${response.status}).`);
  }
  return response.arrayBuffer();
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(offset, offset + chunkSize)),
    );
  }
  return window.btoa(binary);
};

export const renderCandidateAffidavitPdf = async (
  candidate: Record<string, unknown>,
  affidavitType: CandidateAffidavitType,
): Promise<string> => {
  const snapshot = buildAffidavitSnapshot(candidate, affidavitType);
  const [sidebarBytes, signatureBytes] = await Promise.all([
    fetchAsset(affidavitSidebarUrl),
    fetchAsset(torriMontgomerySignatureUrl),
  ]);
  const pdf = await PDFDocument.create();
  const [regular, bold, italic, sidebar, signature] = await Promise.all([
    pdf.embedFont(StandardFonts.Helvetica),
    pdf.embedFont(StandardFonts.HelveticaBold),
    pdf.embedFont(StandardFonts.HelveticaOblique),
    pdf.embedPng(sidebarBytes),
    pdf.embedPng(signatureBytes),
  ]);

  let page!: PDFPage;
  let cursorX = CONTENT_LEFT;
  let cursorY = PAGE_HEIGHT - 34;

  const addPage = () => {
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    page.drawImage(sidebar as PDFImage, { x: 0, y: 0, width: 160, height: PAGE_HEIGHT });
    cursorX = CONTENT_LEFT;
    cursorY = PAGE_HEIGHT - 34;
  };

  const ensureSpace = (height: number) => {
    if (cursorY - height < CONTENT_BOTTOM) addPage();
  };

  const lineBreak = (height = BODY_LINE_HEIGHT) => {
    cursorX = CONTENT_LEFT;
    cursorY -= height;
  };

  const fontForRun = (run: TextRun): PDFFont => {
    if (run.bold) return bold;
    if (run.italic) return italic;
    return regular;
  };

  const writeRuns = (runs: TextRun[], fontSize = BODY_FONT_SIZE, lineHeight = BODY_LINE_HEIGHT) => {
    let pendingSpace = false;
    ensureSpace(lineHeight);
    for (const run of runs) {
      const font = fontForRun(run);
      for (const token of run.content.split(/(\s+)/).filter(Boolean)) {
        if (/^\s+$/.test(token)) {
          pendingSpace = cursorX > CONTENT_LEFT;
          continue;
        }
        const prefix = pendingSpace ? ' ' : '';
        const value = `${prefix}${token}`;
        const width = font.widthOfTextAtSize(value, fontSize);
        if (cursorX > CONTENT_LEFT && cursorX + width > CONTENT_LEFT + CONTENT_WIDTH) {
          lineBreak(lineHeight);
          ensureSpace(lineHeight);
          pendingSpace = false;
        }
        const rendered = pendingSpace && cursorX > CONTENT_LEFT ? value : token;
        page.drawText(rendered, {
          x: cursorX,
          y: cursorY,
          size: fontSize,
          font,
          color: rgb(0, 0, 0),
        });
        cursorX += font.widthOfTextAtSize(rendered, fontSize);
        pendingSpace = true;
      }
    }
    lineBreak(lineHeight);
  };

  const paragraph = (runs: TextRun[] | string, gap = 6.5) => {
    writeRuns(typeof runs === 'string' ? [{ content: runs }] : runs);
    cursorY -= gap;
  };

  addPage();
  writeRuns([{ content: snapshot.generatedDate }], 9.5, 12);
  cursorY -= 3;
  writeRuns([{ content: 'AFFIDAVIT FROM THE OFFICE OF DIVERSION & REENTRY', bold: true }], 11, 14);
  cursorY -= 7;

  if (affidavitType === 'mhd') {
    paragraph([
      { content: 'Based upon an examination of ' },
      { content: snapshot.name, bold: true },
      { content: ', booking number ' },
      { content: snapshot.bookingNumber, bold: true },
      { content: ', a defendant in case number(s) ' },
      { content: snapshot.caseNumber, bold: true },
      { content: ", and my review of the defendant's medical records, arrest reports and other relevant evidence, it is my opinion, in my capacity as a qualified mental health expert, that the defendant satisfies the conditions set forth in Penal Code Section 1001.36(b)-(c)(1) as follows:" },
    ]);
    paragraph([
      { content: 'Defendant is diagnosed with ' },
      { content: snapshot.diagnosis, bold: true },
      { content: ', as defined in the most recent edition of the Diagnostic and Statistical Manual of Mental Disorders.' },
    ]);
    paragraph("Defendant's mental health disorder played a significant role in the commission of the charged offense; and defendant's symptoms motivating the criminal behavior would respond to mental health treatment.");
    paragraph('Pending a finding that the conditions set forth in Penal Code Section 1001.36(b)-(c)(1) have been satisfied, it is recommended that defendant be placed in the Office of Diversion and Reentry (ODR) Mental Health Diversion (MHD) program through which defendant will receive intensive case management services, housing and mental health services. In the event that defendant is not placed in the ODR MHD program, it is alternatively recommended that defendant be evaluated for placement in the ODR Housing program through which defendant will also be able to receive intensive case management services, housing and mental health services.');
  } else {
    paragraph([
      { content: 'Based upon my review of this case, I recommend that ' },
      { content: snapshot.name, bold: true },
      { content: ' booking number ' },
      { content: snapshot.bookingNumber, bold: true },
      { content: ' a defendant in new case number(s) ' },
      { content: snapshot.caseNumber, bold: true },
      { content: ', would best be served by placement in Office of Diversion and Reentry Housing and enrollment into the following program:' },
    ]);
    ensureSpace(20);
    const icmsHeading = 'Intensive Case Management Services (ICMS)';
    page.drawText(icmsHeading, {
      x: PAGE_WIDTH - CONTENT_RIGHT - bold.widthOfTextAtSize(icmsHeading, BODY_FONT_SIZE),
      y: cursorY,
      size: BODY_FONT_SIZE,
      font: bold,
    });
    lineBreak(BODY_LINE_HEIGHT + 6.5);
    paragraph('ICMS provides high quality case management to clients eligible for permanent housing through Housing for Health (HFH). ICMS provides a client-centered approach and excellent customer service that is sensitive to the challenges that homeless persons with a range of medical and behavioral health issues face as they move into and maintain permanent supportive housing. ICMS also employs a “whatever it takes approach” to assist clients in their transition from homelessness to permanent housing.');
    paragraph('ICMS forms the core of the services provided to clients in permanent supportive housing. The clients served will be DHS patients who are homeless and who have a chronic illness or physical disability or are a high utilizer of DHS services. ICMS is designed to assist homeless clients with achieving and maintaining health, mental health, and housing stability. The intensive case manager serves as the central point of contact for the referred client through which care and services are coordinated.');
    paragraph("Intensive case managers assist individuals at every stage of the housing stabilization process. ICMS services are flexible to meet individual needs, and the intensity of services is regularly monitored and adjusted based on each client's level of functioning and acuity of needs. Services may range from highly intensive individualized support as clients transition from homelessness to permanent housing to less intense support for maintaining housing and supportive services.");
    paragraph('ICMS includes, but is not limited to: outreach and engagement; intake and assessment; service planning; housing and rental assistance; linkages to health, mental health, substance use disorder services and other supportive services; ongoing monitoring and follow-up; assistance with benefits establishment, transportation, and legal issues; crisis management; eviction prevention; client education; housing location services; and coordination with Housing for Health partners.');
    paragraph([
      { content: 'Defendant is diagnosed with ' },
      { content: snapshot.diagnosis, bold: true },
      { content: ', as defined in the most recent edition of the Diagnostic and Statistical Manual of Mental Disorders.' },
    ]);
  }

  paragraph([
    { content: 'Comments: ', bold: true },
    { content: "Defendant's next court date in the home court is " },
    { content: snapshot.nextCourtDate, bold: true },
    { content: '. Please wait for the court order with the ODR suitability date before setting the matter in the ODR court Hub.' },
  ]);
  if (snapshot.priorProgram || snapshot.priorCaseNumbers) {
    paragraph([
      { content: `${affidavitType === 'mhd' ? 'Defendant previously participated' : 'Defendant participated'} in the ` },
      { content: snapshot.priorProgram || 'ODR', bold: true },
      { content: ' program on case(s) ' },
      { content: snapshot.priorCaseNumbers || 'not provided', bold: true },
      { content: '.' },
    ]);
  }
  paragraph('If you need further information about this patient or recommendation, please email TMontgomery@dhs.lacounty.gov.', 9);
  ensureSpace(110);
  writeRuns([{ content: affidavitType === 'mhd' ? 'Sincerely,' : 'Many Thanks,' }]);
  page.drawImage(signature as PDFImage, { x: CONTENT_LEFT + 8, y: cursorY - 52, width: 108, height: 53 });
  cursorY -= 62;
  writeRuns([{ content: 'Torri Montgomery, MD', bold: true }], 10, 12);
  writeRuns([{ content: 'Clinical and Forensic Psychiatrist', italic: true }], 9.5, 11.5);
  writeRuns([{ content: 'Office of Diversion and Reentry Associate Medical Director' }], 9.5, 11.5);
  writeRuns([{ content: 'Los Angeles County - Department of Health Services' }], 9.5, 11.5);

  return bytesToBase64(await pdf.save());
};
